import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableCell,
  StatusBadge,
  Grid
} from '../styles/PresenterPortal.styles';
import { ConfirmationModal, SuccessModal, ErrorModal } from './Modal';
import Modal from './Modal';
import { authAPI, presenterAPI } from '../services/api';

// Custom Filter Components
const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 640px) {
    margin-bottom: 2rem;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (min-width: 640px) {
    gap: 1rem;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.theme.colors.border || '#e5e7eb'};
  border-radius: ${props => props.theme.borderRadius.lg || '12px'};
  background: white;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.primary || '#111827'};
  transition: all 0.2s ease;
  min-width: 200px;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary || '#3b82f6'};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (min-width: 640px) {
    font-size: 0.95rem;
  }

  @media (min-width: 768px) {
    width: auto;
    min-width: 200px;
  }
`;

const FilterLabel = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary || '#111827'};
  font-size: 0.85rem;
  white-space: nowrap;

  @media (min-width: 640px) {
    font-size: 0.9rem;
  }
`;

const DrawButton = styled(Button)`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  font-weight: 600;
  padding: 0.875rem 1.5rem;
  font-size: 0.9rem;
  border-radius: ${props => props.theme.borderRadius.lg || '12px'};
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (min-width: 640px) {
    font-size: 0.95rem;
    padding: 1rem 1.75rem;
  }

  @media (min-width: 768px) {
    width: auto;
    padding: 1rem 2rem;
    font-size: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${props => props.theme.colors.text.secondary || '#6b7280'};

  .icon {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    opacity: 0.5;
  }

  h3 {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
    color: ${props => props.theme.colors.text.primary || '#111827'};
  }

  p {
    font-size: 0.9rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
  }

  @media (min-width: 640px) {
    padding: 3.5rem 1.5rem;
    
    .icon {
      font-size: 3.5rem;
    }
    
    h3 {
      font-size: 1.25rem;
    }
    
    p {
      font-size: 0.95rem;
    }
  }

  @media (min-width: 768px) {
    padding: 4rem 2rem;
    
    .icon {
      font-size: 4rem;
    }
    
    h3 {
      font-size: 1.25rem;
    }
    
    p {
      font-size: 0.95rem;
    }
  }
`;

const ItemInfo = styled.div`
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.lg || '12px'};
  border: 2px solid #0ea5e9;
  margin-bottom: 1.5rem;

  h3 {
    color: #0369a1;
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
    word-break: break-word;
  }

  .details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
    margin-top: 1rem;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .value {
    font-size: 0.9rem;
    font-weight: 600;
    color: #0369a1;
  }

  @media (min-width: 640px) {
    padding: 1.25rem;
    
    h3 {
      font-size: 1.25rem;
    }
    
    .details {
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 1rem;
    }
    
    .label {
      font-size: 0.8rem;
    }
    
    .value {
      font-size: 1rem;
    }
  }

  @media (min-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 2rem;
    
    h3 {
      font-size: 1.25rem;
    }
    
    .details {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
    }
    
    .label {
      font-size: 0.85rem;
    }
    
    .value {
      font-size: 1rem;
    }
  }
`;

function PresenterPortal() {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [nextRefresh, setNextRefresh] = useState(null);

  // Countdown timer for next refresh
  const [countdown, setCountdown] = useState(30);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Filtering state
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStartTime, setFilterStartTime] = useState('');
  const [filterEndTime, setFilterEndTime] = useState('');
  const [currentFilters, setCurrentFilters] = useState({});

  // Modal states
  const [showDrawModal, setShowDrawModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [winner, setWinner] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Auction status
  const [auctionClosed, setAuctionClosed] = useState(false);

  // Analytics state for default analytics card
  const [analytics, setAnalytics] = useState({
    total_money_in: 0,
    total_money_out: 0,
    net_amount: 0
  });
  const [assignedUssdCode, setAssignedUssdCode] = useState(null);

  const normalizeAssignedUssdCode = (code) => {
    if (!code) return null;
    if (typeof code === 'string') {
      return { id: code, ussd_code: code };
    }
    if (code?.ussd_code) {
      return { id: code.id || code.ussd_code, ussd_code: code.ussd_code };
    }
    return null;
  };

  const loadAssignedUssdCodes = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      const normalizedCode = normalizeAssignedUssdCode(response?.data?.assigned_ussd_code);
      setAssignedUssdCode(normalizedCode);
    } catch (err) {
      console.error('Error loading assigned USSD codes:', err);
    }
  };

  useEffect(() => {
    let countdownInterval;
    if (selectedItem && nextRefresh) {
      countdownInterval = setInterval(() => {
        const now = new Date();
        const timeLeft = Math.max(0, Math.ceil((nextRefresh - now) / 1000));
        setCountdown(timeLeft);
      }, 1000);
    }
    
    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [selectedItem, nextRefresh]);

  // Load active items on component mount
  useEffect(() => {
    loadAssignedUssdCodes();
    loadActiveItems();
  }, []);

  // Auto refresh effect - consolidated to avoid conflicts
  // Modified to preserve filters during auto-refresh
  useEffect(() => {
    let refreshInterval;
    if (selectedItem) {
      refreshInterval = setInterval(() => {
        // Check if filters are active and preserve them during auto-refresh
        if (isFiltering && Object.keys(currentFilters).length > 0) {
          // If filters are active, pass them to loadActiveItems to preserve them
          loadActiveItems(currentFilters);
        } else {
          // If no filters are active, load without filters
          loadActiveItems();
        }
      }, 30000); // Refresh every 30 seconds
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [selectedItem, isFiltering, currentFilters]);

  const loadActiveItems = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await presenterAPI.getActiveItems(filters);
      if (response.data) {
        const newItems = response.data.items || [];
        setItems(newItems);

        if ('assigned_ussd_code' in response.data) {
          setAssignedUssdCode(normalizeAssignedUssdCode(response.data.assigned_ussd_code));
        }
        
        // Extract analytics data from the response
        if (response.data.total_money_in !== undefined || response.data.total_money_out !== undefined || response.data.net_amount !== undefined) {
          setAnalytics({
            total_money_in: response.data.total_money_in || 0,
            total_money_out: response.data.total_money_out || 0,
            net_amount: response.data.net_amount || 0
          });
        }
        
        // If there's a selected item, reload its transactions with the fresh data
        if (selectedItem) {
          await loadTransactionsWithData(selectedItem, newItems);
        }
      }
    } catch (err) {
      console.error('Error loading items:', err);
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Load transactions when item is selected
  useEffect(() => {
    if (selectedItem) {
      loadTransactions(selectedItem);
    } else {
      setTransactions([]);
    }
  }, [selectedItem]);

  // Helper function to load transactions with fresh data
  const loadTransactionsWithData = async (itemId, itemsData) => {
    try {
      setLoadingTransactions(true);
      
      // Use the provided items data instead of the state
      const selectedItemData = itemsData.find(item => item.id === itemId);
      if (selectedItemData) {
        let allTransactions = [];
        
        // Transform bids data to match the expected format
        if (selectedItemData.bids && selectedItemData.bids.length > 0) {
          const transformedBids = selectedItemData.bids.map((bid, index) => ({
            id: `${itemId}-bid-${index}`,
            bidder_name: bid.customer_name,
            phone: bid.phone_number,
            bid_amount: bid.amount,
            money_in: selectedItemData.auction_fee || 0, // Auction fee as money in
            money_out: 0, // No money out for bids
            timestamp: bid.bid_time,
            status: 'active',
            bid_count: bid.bid_count,
            transaction_type: 'bid'
          }));
          allTransactions = [...transformedBids];
        }
        
        // Transform money out transactions
        if (selectedItemData.money_out_transactions && selectedItemData.money_out_transactions.length > 0) {
          const transformedMoneyOut = selectedItemData.money_out_transactions.map((transaction, index) => ({
            id: `${itemId}-moneyout-${index}`,
            bidder_name: transaction.name,
            phone: transaction.phone_number,
            bid_amount: 0, // No bid amount for money out transactions
            money_in: 0, // No money in for money out transactions
            money_out: transaction.amount, // Money out amount
            timestamp: transaction.transaction_time,
            status: 'money_out',
            bid_count: transaction.bid_count || 0,
            transaction_type: 'money_out'
          }));
          allTransactions = [...allTransactions, ...transformedMoneyOut];
        }
        
        // Sort all transactions by timestamp (newest first)
        allTransactions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        setTransactions(allTransactions);
        
        setLastRefresh(new Date());
        setNextRefresh(new Date(Date.now() + 30000)); // 30 seconds from now
      } else {
        setTransactions([]);
        setLastRefresh(new Date());
        setNextRefresh(new Date(Date.now() + 30000));
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const loadTransactions = async (itemId) => {
    // Use the helper function with current items state
    await loadTransactionsWithData(itemId, items);
  };

  const handleDrawWinner = () => {
    const activeBids = transactions.filter(transaction => 
      (transaction.status === 'active' || transaction.status === 'Active') && 
      transaction.transaction_type !== 'money_out'
    );
    
    if (activeBids.length === 0) {
      setErrorMessage('No active bids found for this item!');
      setShowErrorModal(true);
      return;
    }

    setShowDrawModal(true);
  };

  const confirmDraw = async () => {
    try {
      setIsDrawing(true);
      setShowDrawModal(false);
      
      // Call the close-bid API endpoint
      const response = await presenterAPI.closeBid(selectedItem);
      
      if (response.data && response.data.success && response.data.winner) {
        const winnerData = response.data.winner;
        
        // Transform the server response to match our expected format
        const transformedWinner = {
          id: Date.now(), // Generate a temporary ID
          name: winnerData.name,
          phone: winnerData.msisdn,
          bid_amount: parseFloat(winnerData.amount.replace('KES ', '').replace(',', '')),
          item_name: winnerData.item,
          price: winnerData.price,
          timestamp: new Date().toISOString()
        };
        
        setWinner(transformedWinner);
        setSuccessMessage(`Auction closed successfully! ${winnerData.name} has been awarded ${winnerData.item} with a bid of ${winnerData.amount}. The item price is ${winnerData.price}.`);
        setShowSuccessModal(true);
        setAuctionClosed(true);
        
        // Reload transactions to update status
        loadTransactions(selectedItem);
      } else {
        // Fallback to dummy data if API doesn't return winner info
        const activeBids = transactions.filter(transaction => 
          (transaction.status === 'active' || transaction.status === 'Active') && 
          transaction.transaction_type !== 'money_out'
        );
        const randomIndex = Math.floor(Math.random() * activeBids.length);
        const selectedWinner = activeBids[randomIndex];
        
        const dummyWinner = {
          id: selectedWinner.id,
          name: selectedWinner.bidder_name || selectedWinner.name,
          phone: selectedWinner.phone || selectedWinner.phone_number,
          bid_amount: selectedWinner.bid_amount,
          item_name: selectedItemDetails?.item_name || 'Selected Item',
          price: formatCurrency(selectedWinner.bid_amount),
          timestamp: new Date().toISOString()
        };
        
        setWinner(dummyWinner);
        setSuccessMessage(`Auction closed successfully! ${dummyWinner.name} has been awarded ${dummyWinner.item_name} with a bid of ${formatCurrency(dummyWinner.bid_amount)}.`);
        setShowSuccessModal(true);
        setAuctionClosed(true);
        
        // Reload transactions to update status
        loadTransactions(selectedItem);
      }
    } catch (err) {
      console.error('Error drawing winner:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    } finally {
      setIsDrawing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]}-${match[2]}-${match[3]}-${match[4]}`;
    }
    return phone;
  };

  // Helper function to format error messages
  const formatErrorMessage = (error) => {
    if (typeof error === 'string') return error;
    
    // Use the custom error message if available
    if (error.message) {
      return error.message;
    }
    
    // Fallback to generic message
    return 'An unexpected error occurred. Please try again.';
  };

  const selectedItemDetails = items.find(item => item.id === selectedItem);
  const hasAssignedUssdCode = Boolean(assignedUssdCode?.ussd_code);
  
  // Use all transactions since filtering is now done on the backend
  const displayTransactions = transactions;
  
  const activeBids = displayTransactions.filter(transaction => 
    (transaction.status === 'active' || transaction.status === 'Active') && 
    transaction.transaction_type !== 'money_out'
  );

  // Pagination logic
  const indexOfLastTransaction = currentPage * itemsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - itemsPerPage;
  const currentTransactions = displayTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(displayTransactions.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Backend filtering functions
  const applyFilters = async () => {
    const filters = {};
    
    if (filterType) {
      filters.filterType = filterType;
    }
    if (filterStartDate) {
      filters.startDate = filterStartDate;
    }
    if (filterEndDate) {
      filters.endDate = filterEndDate;
    }
    if (filterStartTime) {
      filters.startTime = filterStartTime;
    }
    if (filterEndTime) {
      filters.endTime = filterEndTime;
    }

    // Check if any filters are applied
    const hasFilters = Object.keys(filters).length > 0;
    
    if (hasFilters) {
      setIsFiltering(true);
      setCurrentFilters(filters);
      setCurrentPage(1);
      await loadActiveItems(filters);
    } else {
      clearFilters();
    }
  };

  const clearFilters = async () => {
    setFilterType('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterStartTime('');
    setFilterEndTime('');
    setCurrentFilters({});
    setIsFiltering(false);
    setCurrentPage(1);
    await loadActiveItems(); // Load without filters
  };

  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    await applyFilters();
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Presenter Portal</PageTitle>
        <PageSubtitle>
          {hasAssignedUssdCode
            ? 'Select an item to view bids and award buyers'
            : 'No shortcode assigned yet. Please contact your administrator.'}
        </PageSubtitle>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
          {hasAssignedUssdCode ? (
            <span
              style={{
                display: 'inline-block',
                fontSize: '0.8rem',
                padding: '0.3rem 0.6rem',
                borderRadius: '999px',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                color: '#1d4ed8',
                fontWeight: '600'
              }}
            >
              {assignedUssdCode.ussd_code}
            </span>
          ) : (
            <span
              style={{
                display: 'inline-block',
                fontSize: '0.8rem',
                padding: '0.3rem 0.6rem',
                borderRadius: '999px',
                backgroundColor: '#fff7ed',
                border: '1px solid #fdba74',
                color: '#9a3412',
                fontWeight: '600'
              }}
            >
              No shortcode assigned
            </span>
          )}
        </div>
      </PageHeader>

      {/* Default Analytics Card */}
      <Card style={{ 
        marginBottom: '1.5rem', 
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <CardHeader>
          <CardTitle style={{ 
            color: '#111827', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontSize: '1.25rem',
            fontWeight: '600'
          }}>
            📊 Today's Overview
          </CardTitle>
          <CardDescription style={{ 
            color: '#6b7280',
            fontSize: '0.9rem'
          }}>
            Real-time financial summary for all items
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            padding: '0.5rem 0'
          }}>
            <div style={{
              textAlign: 'center',
              padding: '1.5rem 1rem',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px'
            }}>
              <div style={{ 
                fontSize: '0.85rem', 
                color: '#64748b', 
                marginBottom: '0.75rem',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                💰 Total Money In
              </div>
              <div style={{ 
                fontSize: '1.75rem', 
                fontWeight: '700',
                color: '#059669'
              }}>
                {formatCurrency(analytics.total_money_in)}
              </div>
            </div>
            
            <div style={{
              textAlign: 'center',
              padding: '1.5rem 1rem',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px'
            }}>
              <div style={{ 
                fontSize: '0.85rem', 
                color: '#64748b', 
                marginBottom: '0.75rem',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                💸 Total Money Out
              </div>
              <div style={{ 
                fontSize: '1.75rem', 
                fontWeight: '700',
                color: '#dc2626'
              }}>
                {formatCurrency(analytics.total_money_out)}
              </div>
            </div>
            
            <div style={{
              textAlign: 'center',
              padding: '1.5rem 1rem',
              backgroundColor: analytics.net_amount >= 0 ? '#f0fdf4' : '#fef2f2',
              border: analytics.net_amount >= 0 ? '1px solid #bbf7d0' : '1px solid #fecaca',
              borderRadius: '12px'
            }}>
              <div style={{ 
                fontSize: '0.85rem', 
                color: '#64748b', 
                marginBottom: '0.75rem',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {analytics.net_amount >= 0 ? '📈' : '📉'} Net Amount
              </div>
              <div style={{ 
                fontSize: '1.75rem', 
                fontWeight: '700',
                color: analytics.net_amount >= 0 ? '#059669' : '#dc2626'
              }}>
                {formatCurrency(analytics.net_amount)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>



      {/* Item Filter */}
      <FilterContainer>
        <FilterGroup>
          <FilterLabel htmlFor="item-select">Select Item:</FilterLabel>
          <Select
            id="item-select"
            value={selectedItem}
            onChange={(e) => {
              setSelectedItem(e.target.value);
              setAuctionClosed(false); // Reset auction status when new item is selected
              setWinner(null); // Reset winner when new item is selected
            }}
            disabled={loading || !hasAssignedUssdCode}
          >
            <option value="">Choose an item to view bids...</option>
            {items.map(item => (
              <option key={item.id} value={item.id}>
                {item.item_name} - {formatCurrency(item.item_price || 0)} per bid
              </option>
            ))}
          </Select>
        </FilterGroup>

        {selectedItem && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <DrawButton
              onClick={handleDrawWinner}
              disabled={activeBids.length === 0 || isDrawing || auctionClosed}
            >
              {isDrawing ? '🎲 Awarding...' : auctionClosed ? '✅ Auction Closed' : '🎯 Award Buyer'}
            </DrawButton>
            
            <Button
              variant="outline"
              onClick={loadActiveItems}
              disabled={loading}
              style={{ 
                padding: '0.75rem 1rem',
                fontSize: '0.9rem',
                minWidth: 'auto'
              }}
            >
              {loading ? '🔄' : '🔄 Refresh'}
            </Button>
          </div>
        )}
      </FilterContainer>

      {/* Selected Item Information */}
      {selectedItemDetails && (
        <ItemInfo>
          <h3>{selectedItemDetails.item_name}</h3>
          <div className="details">
            <div className="detail-item">
              <span className="label">Money In</span>
              <span className="value">{formatCurrency(selectedItemDetails.overview?.money_in || 0)}</span>
            </div>
            <div className="detail-item">
              <span className="label">Money Out</span>
              <span className="value">{formatCurrency(selectedItemDetails.overview?.money_out || 0)}</span>
            </div>
            <div className="detail-item">
              <span className="label">Expected Return</span>
              <span className="value">{formatCurrency(selectedItemDetails.overview?.expected_return || 0)}</span>
            </div>
            <div className="detail-item">
              <span className="label">Total Bids</span>
              <span className="value">{selectedItemDetails.overview?.total_bids || 0}</span>
            </div>
          </div>
        </ItemInfo>
      )}

      {/* Winner Display */}
      {winner && (
        <>
          {/* Success Notification */}
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#10b981',
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            maxWidth: '400px',
            animation: 'slideInRight 0.5s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ fontSize: '1.5rem' }}>🎉</div>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                  Auction Closed Successfully!
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                  The server has confirmed the auction closure
                </div>
              </div>
            </div>
          </div>
          
                    <Card style={{ marginBottom: '1.5rem', backgroundColor: '#f0fdf4', border: '2px solid #10b981' }}>
          <CardHeader>
            <CardTitle style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              🏆 Auction Closed
            </CardTitle>
            <CardDescription style={{ color: '#059669' }}>
              The auction has been closed and a buyer has been awarded the item
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ 
              padding: '1.5rem',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #d1fae5',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ 
                    minWidth: '120px', 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '0.9rem'
                  }}>
                    Name:
                  </div>
                  <div style={{ 
                    color: '#10b981', 
                    fontWeight: '600', 
                    fontSize: '1.1rem',
                    flex: 1
                  }}>
                    {winner.name}
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ 
                    minWidth: '120px', 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '0.9rem'
                  }}>
                    Amount:
                  </div>
                  <div style={{ 
                    color: '#10b981', 
                    fontWeight: '600', 
                    fontSize: '1.1rem',
                    flex: 1
                  }}>
                    {winner.amount ? winner.amount : formatCurrency(winner.bid_amount)}
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ 
                    minWidth: '120px', 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '0.9rem'
                  }}>
                    MSISDN:
                  </div>
                  <div style={{ 
                    color: '#059669', 
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    flex: 1
                  }}>
                    {formatPhoneNumber(winner.phone)}
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ 
                    minWidth: '120px', 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '0.9rem'
                  }}>
                    Item:
                  </div>
                  <div style={{ 
                    color: '#059669', 
                    fontWeight: '500',
                    fontSize: '1rem',
                    flex: 1
                  }}>
                    {winner.item_name || selectedItemDetails?.item_name}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </>
      )}

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <CardTitle>
                {selectedItem ? 'All Transactions' : 'Live Bidding Activity'}
              </CardTitle>
              <CardDescription>
                {selectedItem 
                  ? `Bids and money out transactions for ${selectedItemDetails?.item_name || 'selected item'}`
                  : 'Select an item above to view all transactions'
                }
              </CardDescription>
            </div>
            
            {selectedItem && lastRefresh && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                fontSize: '0.85rem',
                color: '#64748b'
              }}>
                {isFiltering ? (
                  <>
                    <span>🔍 Filter Mode Active</span>
                    <span>•</span>
                    <span>Auto-refresh paused</span>
                    <span>•</span>
                    <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
                  </>
                ) : (
                  <>
                    <span>🔄 Auto-refresh every 30s</span>
                    <span>•</span>
                    <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
                    <span>•</span>
                    <span>Next refresh in: {countdown}s</span>
                  </>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        
        {/* Filter Controls */}
        {selectedItem && (
          <div style={{ 
            padding: '1rem', 
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            <form onSubmit={handleFilterSubmit} style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem', 
              alignItems: 'end' 
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Transaction Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    backgroundColor: 'white',
                    width: '100%'
                  }}
                >
                  <option value="">All Transactions</option>
                  <option value="money_in">Money In Only</option>
                  <option value="money_out">Money Out Only</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Start Date</label>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    width: '100%'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>End Date</label>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    width: '100%'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Start Time</label>
                <input
                  type="time"
                  value={filterStartTime}
                  onChange={(e) => setFilterStartTime(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    width: '100%'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>End Time</label>
                <input
                  type="time"
                  value={filterEndTime}
                  onChange={(e) => setFilterEndTime(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    width: '100%'
                  }}
                />
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem',
                gridColumn: '1 / -1',
                justifyContent: 'center'
              }}>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', minWidth: '120px' }}
                >
                  🔍 Apply Filters
                </Button>
                
                {isFiltering && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      fontSize: '0.9rem',
                      backgroundColor: transactions.length === 0 ? '#fef2f2' : 'white',
                      borderColor: transactions.length === 0 ? '#fecaca' : undefined,
                      color: transactions.length === 0 ? '#dc2626' : undefined,
                      minWidth: '120px'
                    }}
                  >
                    ❌ Clear Filters
                  </Button>
                )}
              </div>
            </form>
            
            {isFiltering && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem', 
                backgroundColor: transactions.length === 0 ? '#fef2f2' : '#dbeafe', 
                border: transactions.length === 0 ? '1px solid #fecaca' : '1px solid #93c5fd',
                borderRadius: '6px',
                fontSize: '0.85rem',
                color: transactions.length === 0 ? '#dc2626' : '#1e40af',
                wordBreak: 'break-word'
              }}>
                <strong>Filter Active:</strong> {transactions.length === 0 ? 'No transactions found' : `Showing ${transactions.length} transactions`}
                <div style={{ marginTop: '0.25rem' }}>
                  {filterType && <span style={{ display: 'inline-block', marginRight: '0.5rem' }}>Type: {filterType === 'money_in' ? 'Money In Only' : filterType === 'money_out' ? 'Money Out Only' : 'All Transactions'}</span>}
                  {filterStartDate && <span style={{ display: 'inline-block', marginRight: '0.5rem' }}>From: {filterStartDate}</span>}
                  {filterEndDate && <span style={{ display: 'inline-block', marginRight: '0.5rem' }}>To: {filterEndDate}</span>}
                  {filterStartTime && <span style={{ display: 'inline-block', marginRight: '0.5rem' }}>Time: {filterStartTime}</span>}
                  {filterEndTime && <span style={{ display: 'inline-block', marginRight: '0.5rem' }}>- {filterEndTime}</span>}
                </div>
                {transactions.length === 0 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                    💡 Try adjusting your filters or click "Clear Filters" to see all transactions
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        <CardContent>
          {loading ? (
            <EmptyState>
              <div className="icon">⏳</div>
              <h3>Loading Items...</h3>
              <p>Please wait while we fetch the available items.</p>
            </EmptyState>
          ) : error ? (
            <EmptyState>
              <div className="icon">❌</div>
              <h3>Error Loading Items</h3>
              <p>{error}</p>
            </EmptyState>
          ) : !hasAssignedUssdCode ? (
            <EmptyState>
              <div className="icon">ℹ️</div>
              <h3>No shortcode assigned</h3>
              <p>No shortcode assigned yet. Please contact your administrator.</p>
            </EmptyState>
          ) : !selectedItem ? (
            <EmptyState>
              <div className="icon">🎯</div>
              <h3>Select an Item</h3>
              <p>Choose an item from the dropdown above to view its bidding transactions and award buyers.</p>
            </EmptyState>
          ) : loadingTransactions ? (
            <EmptyState>
              <div className="icon">🔄</div>
              <h3>Loading Transactions...</h3>
              <p>Fetching the latest bidding data for {selectedItemDetails?.item_name}.</p>
            </EmptyState>
          ) : transactions.length === 0 ? (
            <EmptyState>
              <div className="icon">{isFiltering ? '🔍' : '📋'}</div>
              <h3>{isFiltering ? 'No Transactions Found' : 'No Transactions Yet'}</h3>
              <p>
                {isFiltering 
                  ? 'No transactions match your current filters. Try adjusting your filter criteria or clear all filters to see all transactions.'
                  : 'There are currently no bids or money out transactions for this item. Transactions will appear here as they come in.'
                }
              </p>
            </EmptyState>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Phone Number</TableHeaderCell>
                  <TableHeaderCell>Money In</TableHeaderCell>
                  <TableHeaderCell>Money Out</TableHeaderCell>
                  <TableHeaderCell>Count</TableHeaderCell>
                  <TableHeaderCell>Time</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <tbody>
                {currentTransactions.map(transaction => (
                  <TableRow key={transaction.id}>
                    <TableCell data-label="Name">
                      <strong>{transaction.bidder_name || transaction.name || 'Unknown'}</strong>
                    </TableCell>
                    <TableCell data-label="Phone Number">
                      <span style={{ fontFamily: 'monospace' }}>
                        {formatPhoneNumber(transaction.phone || transaction.phone_number || 'N/A')}
                      </span>
                    </TableCell>
                    <TableCell data-label="Money In">
                      <strong style={{ 
                        color: transaction.transaction_type === 'money_out' ? '#6b7280' : 
                               transaction.status === 'active' ? '#10b981' : '#6b7280', 
                        fontSize: '1.1rem' 
                      }}>
                        {transaction.transaction_type === 'money_out' ? '-' : formatCurrency(transaction.bid_amount || 0)}
                      </strong>
                    </TableCell>
                    <TableCell data-label="Money Out">
                      <strong style={{ 
                        color: '#dc2626', 
                        fontSize: '1.1rem' 
                      }}>
                        {transaction.transaction_type === 'money_out' ? formatCurrency(transaction.money_out || 0) : '-'}
                      </strong>
                    </TableCell>
                    <TableCell data-label="Count">
                      <span style={{ 
                        backgroundColor: transaction.transaction_type === 'money_out' ? '#fef2f2' : '#f3f4f6', 
                        color: transaction.transaction_type === 'money_out' ? '#dc2626' : '#374151',
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}>
                        {transaction.bid_count || (transaction.transaction_type === 'money_out' ? 0 : 1)}
                      </span>
                    </TableCell>
                    <TableCell data-label="Time">
                      {formatDate(transaction.timestamp || transaction.created_at || new Date())}
                    </TableCell>
                    <TableCell data-label="Status">
                      <StatusBadge status={transaction.status || 'pending'}>
                        {transaction.transaction_type === 'money_out' ? 'Money Out' :
                         transaction.status === 'active' ? 'Active' : 
                         transaction.status === 'winner' ? 'Winner' : 
                         transaction.status === 'outbid' ? 'Outbid' :
                         transaction.status || 'Pending'}
                      </StatusBadge>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
          
          {/* Pagination Controls */}
          {transactions.length > itemsPerPage && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '1rem', 
              marginTop: '2rem',
              padding: '1rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                >
                  ← Prev
                </Button>
                
                <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map(pageNumber => (
                    <Button
                      key={pageNumber}
                      variant={currentPage === pageNumber ? "primary" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(pageNumber)}
                      style={{ 
                        padding: '0.5rem 0.5rem',
                        minWidth: '2rem',
                        fontSize: '0.8rem'
                      }}
                    >
                      {pageNumber}
                    </Button>
                  ))}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                >
                  Next →
                </Button>
              </div>
              
              <div style={{ 
                fontSize: '0.8rem', 
                color: '#64748b',
                textAlign: 'center',
                lineHeight: '1.4'
              }}>
                Showing {indexOfFirstTransaction + 1}-{Math.min(indexOfLastTransaction, displayTransactions.length)} of {displayTransactions.length} transactions
                {isFiltering && <div style={{ marginTop: '0.25rem' }}>(filtered from {transactions.length} total)</div>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Draw Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDrawModal}
        onClose={() => setShowDrawModal(false)}
        onConfirm={confirmDraw}
        title="award buyer"
        message={`Are you sure you want to close the auction for "${selectedItemDetails?.item_name}"? This action cannot be undone.`}
        confirmText="End Auction"
        cancelText="Cancel"
        variant="primary"
      />

      {/* Drawing Progress Modal */}
      {isDrawing && (
        <Modal isOpen={isDrawing} onClose={() => {}} title="🎲 Drawing Winner..." showCloseButton={false}>
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{
              width: '80px',
              height: '80px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #10b981',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 2rem'
            }}></div>
            <h3 style={{ 
              marginBottom: '1rem',
              color: '#1e293b',
              fontSize: '1.25rem'
            }}>
              Selecting Winner...
            </h3>
            <p style={{ 
              color: '#64748b',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              maxWidth: '300px',
              margin: '0 auto'
            }}>
              Please wait while we randomly select the winner from all active bids for{' '}
              <strong>{selectedItemDetails?.item_name}</strong>.
            </p>
          </div>
          
          <style jsx>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </Modal>
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Winner Drawn!"
        message={successMessage}
        buttonText="OK"
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        buttonText="OK"
      />
    </PageContainer>
  );
}

export default PresenterPortal; 
